import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../state/auth/useAuth';
import { useToast } from '../../hooks/useToast';
import { ToastContainer } from '../../components/ui/Toast';
import Header from '../../components/ui/Header';
import NavigationPreventer from '../../components/NavigationPreventer';
import { appointmentAPI } from '../../api';

// Import custom hooks
import {
  usePatientQueue,
  usePatientData,
  usePrescription
} from './hooks';// Import components
import {
  Sidebar,
  MobileNav,
  PatientDetails
} from './components';

import TopNavBar from './components/TopNavBar';
import HorizontalPatientQueue from './components/HorizontalPatientQueue';

// Import tab components
import {
  FrontPageTab,
  PrescriptionTab,
  DoctorOpinionTab,
  ReferenceTab,
  AvailabilityTab
} from './components/tabs';

// Import Profile component
import Profile from './Profile';

const DoctorDashboard = () => {
  const { user } = useAuth();
  const { toasts, showSuccess, showError, showWarning, removeToast } = useToast();
  const [activeTab, setActiveTab] = useState('frontpage');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [frontPageSnapshot, setFrontPageSnapshot] = useState(null);
  const [frontPageSnapshotMeta, setFrontPageSnapshotMeta] = useState({ patientId: null, timestamp: 0 });
  const [isFrontPageDirty, setIsFrontPageDirty] = useState(false);
  const [pendingTab, setPendingTab] = useState(null);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [unsavedActionLoading, setUnsavedActionLoading] = useState(false);

  // Track unsaved changes in vitals and patient details
  const [hasUnsavedVitals, setHasUnsavedVitals] = useState(false);
  const [hasUnsavedPatientDetails, setHasUnsavedPatientDetails] = useState(false);
  const [pendingPatient, setPendingPatient] = useState(null);
  const [unsavedChangeType, setUnsavedChangeType] = useState(null); // 'vitals' | 'details' | 'form' | 'multiple' | 'prescription'

  // Track prescription changes
  const [prescriptionSnapshot, setPrescriptionSnapshot] = useState(null);
  const [prescriptionSnapshotMeta, setPrescriptionSnapshotMeta] = useState({ patientId: null, timestamp: 0 });
  const [isPrescriptionDirty, setIsPrescriptionDirty] = useState(false);

  // Custom hooks
  const {
    patientQueue,
    queueStats,
    loading: queueLoading,
    fetchQueue,
    handleStartService,
    handleCompleteService,
    handleJoinQueue,
    handleMarkArrived,
    handleAutoJoin
  } = usePatientQueue(user?.id);

  const {
    patientData,
    setPatientData,
    loading: patientLoading,
    loadPatientData,
    resetPatientData,
    handleSavePatient: savePatientDetails,
    handleCreatePatient,
    updateAppointmentComplaint,
    currentAppointmentId,
    calculateAge
  } = usePatientData();

  const {
    prescriptionData,
    setPrescriptionData,
    previousPrescriptions,
    loadingPrescriptions,
    loading: prescriptionLoading,
    diseases,
    slogans,
    medicines,
    tests,
    imagingTests,
    loadingMasterData,
    fetchPreviousPrescriptions,
    addMedicationItem,
    removeMedicationItem,
    updateMedicationItem,
    handleSavePrescription,
    handlePrintPrescription
  } = usePrescription();

  const extractPatientId = useCallback((appointment) => {
    if (!appointment) return null;
    return appointment._id || appointment.appointmentId || appointment.id || null;
  }, []);

  const serializePatientData = useCallback((data) => {
    try {
      return JSON.stringify(data ?? {});
    } catch (error) {
      return '"serialization-error"';
    }
  }, []);

  const markFrontPageClean = useCallback((patientOverride, dataOverride) => {
    const patientId = extractPatientId(patientOverride ?? selectedPatient);
    setFrontPageSnapshot(serializePatientData(dataOverride ?? patientData));
    setFrontPageSnapshotMeta({ patientId, timestamp: Date.now() });
    setIsFrontPageDirty(false);
  }, [extractPatientId, patientData, selectedPatient, serializePatientData]);

  const markPrescriptionClean = useCallback((patientOverride, dataOverride) => {
    const patientId = extractPatientId(patientOverride ?? selectedPatient);
    setPrescriptionSnapshot(serializePatientData(dataOverride ?? prescriptionData));
    setPrescriptionSnapshotMeta({ patientId, timestamp: Date.now() });
    setIsPrescriptionDirty(false);
  }, [extractPatientId, prescriptionData, selectedPatient, serializePatientData]);

  const handleTabChange = useCallback((nextTab) => {
    if (!nextTab || nextTab === activeTab) {
      return;
    }

    // Check for any unsaved changes
    const hasAnyUnsavedChanges = isFrontPageDirty || hasUnsavedVitals || hasUnsavedPatientDetails || isPrescriptionDirty;

    // Check for unsaved changes on Front Page tab
    if (activeTab === 'frontpage' && (isFrontPageDirty || hasUnsavedVitals || hasUnsavedPatientDetails)) {
      setPendingTab(nextTab);
      setPendingPatient(null);

      // Determine what type of changes are unsaved
      const unsavedTypes = [];
      if (hasUnsavedVitals) unsavedTypes.push('vitals');
      if (hasUnsavedPatientDetails) unsavedTypes.push('details');
      if (isFrontPageDirty) unsavedTypes.push('form');

      setUnsavedChangeType(unsavedTypes.length > 1 ? 'multiple' : unsavedTypes[0]);
      setShowUnsavedModal(true);
      return;
    }

    // Check for unsaved changes on Prescription tab
    if (activeTab === 'prescription' && isPrescriptionDirty) {
      setPendingTab(nextTab);
      setPendingPatient(null);
      setUnsavedChangeType('prescription');
      setShowUnsavedModal(true);
      return;
    }

    setActiveTab(nextTab);
  }, [activeTab, isFrontPageDirty, hasUnsavedVitals, hasUnsavedPatientDetails, isPrescriptionDirty]);

  const closeUnsavedModal = useCallback(() => {
    setShowUnsavedModal(false);
    setPendingTab(null);
    setUnsavedActionLoading(false);
  }, []);

  const handleDiscardAllChanges = useCallback(async () => {
    // Discard form changes
    if (frontPageSnapshot) {
      try {
        const parsed = JSON.parse(frontPageSnapshot);
        setPatientData(parsed);
        markFrontPageClean(selectedPatient, parsed);
      } catch (error) {
        console.error('Error reverting patient data:', error);
      }
    }

    // Discard prescription changes
    if (prescriptionSnapshot) {
      try {
        const parsed = JSON.parse(prescriptionSnapshot);
        setPrescriptionData(parsed);
        markPrescriptionClean(selectedPatient, parsed);
      } catch (error) {
        console.error('Error reverting prescription data:', error);
      }
    }

    // Reset unsaved flags
    setHasUnsavedVitals(false);
    setHasUnsavedPatientDetails(false);
    setIsPrescriptionDirty(false);

    const targetTab = pendingTab;
    const targetPatient = pendingPatient;
    closeUnsavedModal();

    // Handle tab change if pending
    if (targetTab) {
      setActiveTab(targetTab);
    }

    // Handle patient change if pending
    if (targetPatient) {
      await proceedWithPatientSelection(targetPatient);
    }
  }, [frontPageSnapshot, prescriptionSnapshot, pendingTab, pendingPatient, closeUnsavedModal, markFrontPageClean, selectedPatient, setPatientData, setPrescriptionData]);

  // Keep old name for backward compatibility
  const handleDiscardFrontPageChanges = handleDiscardAllChanges;

  useEffect(() => {
    if (!frontPageSnapshot) {
      setIsFrontPageDirty(false);
      return;
    }
    const currentSnapshot = serializePatientData(patientData);
    setIsFrontPageDirty(currentSnapshot !== frontPageSnapshot);
  }, [patientData, frontPageSnapshot, serializePatientData]);

  useEffect(() => {
    const currentPatientId = extractPatientId(selectedPatient);

    if (!selectedPatient) {
      if (frontPageSnapshot !== null) {
        setFrontPageSnapshot(null);
        setFrontPageSnapshotMeta({ patientId: null, timestamp: Date.now() });
      }
      setIsFrontPageDirty(false);
      return;
    }

    if (patientLoading) return;

    if (frontPageSnapshotMeta.patientId !== currentPatientId) {
      markFrontPageClean(selectedPatient, patientData);
    }
  }, [selectedPatient, patientLoading, patientData, frontPageSnapshotMeta.patientId, extractPatientId, markFrontPageClean]);

  // Track prescription dirty state
  useEffect(() => {
    if (!prescriptionSnapshot) {
      setIsPrescriptionDirty(false);
      return;
    }
    const currentSnapshot = serializePatientData(prescriptionData);
    setIsPrescriptionDirty(currentSnapshot !== prescriptionSnapshot);
  }, [prescriptionData, prescriptionSnapshot, serializePatientData]);

  // Initialize prescription snapshot when patient changes
  useEffect(() => {
    const currentPatientId = extractPatientId(selectedPatient);

    if (!selectedPatient) {
      if (prescriptionSnapshot !== null) {
        setPrescriptionSnapshot(null);
        setPrescriptionSnapshotMeta({ patientId: null, timestamp: Date.now() });
      }
      setIsPrescriptionDirty(false);
      return;
    }

    if (patientLoading || prescriptionLoading) return;

    if (prescriptionSnapshotMeta.patientId !== currentPatientId) {
      markPrescriptionClean(selectedPatient, prescriptionData);
    }
  }, [selectedPatient, patientLoading, prescriptionLoading, prescriptionData, prescriptionSnapshotMeta.patientId, extractPatientId, markPrescriptionClean]);



  // Tab definitions
  const tabs = [
    { id: 'frontpage', label: 'Patient Details', icon: 'user' },
    { id: 'prescription', label: 'Prescription', icon: 'clipboard' },
    { id: 'all-patients', label: 'Manage Patients', icon: 'users' },
    { id: 'reference', label: 'Reference', icon: 'share' },
    // { id: 'doctor-opinion', label: 'Doctor Opinion', icon: 'message-square' },
    { id: 'availability', label: 'My Schedule', icon: 'calendar' },
    { id: 'profile', label: 'My Profile', icon: 'user-circle' },
  ];

  // Handle patient selection
  const handlePatientSelect = async (appointment) => {
    // Check for any unsaved changes before switching patients
    const hasAnyUnsavedChanges = isFrontPageDirty || hasUnsavedVitals || hasUnsavedPatientDetails || isPrescriptionDirty;

    if (hasAnyUnsavedChanges && selectedPatient) {
      setPendingPatient(appointment);
      setPendingTab(null);

      // Determine what type of changes are unsaved
      const unsavedTypes = [];
      if (hasUnsavedVitals) unsavedTypes.push('vitals');
      if (hasUnsavedPatientDetails) unsavedTypes.push('details');
      if (isFrontPageDirty) unsavedTypes.push('form');
      if (isPrescriptionDirty) unsavedTypes.push('prescription');

      setUnsavedChangeType(unsavedTypes.length > 1 ? 'multiple' : unsavedTypes[0]);
      setShowUnsavedModal(true);
      return;
    }

    // No unsaved changes, proceed with selection
    await proceedWithPatientSelection(appointment);
  };

  const proceedWithPatientSelection = async (appointment) => {
    setSelectedPatient(appointment);
    // Ensure the details tab is visible
    setActiveTab('frontpage');
    await loadPatientData(appointment);
    // Also fetch previous prescriptions when patient is selected
    fetchPreviousPrescriptions(appointment.patient._id || appointment.patient);
    // Reset unsaved change flags
    setHasUnsavedVitals(false);
    setHasUnsavedPatientDetails(false);
  };

  // Handle service start with toast notification
  const handleServiceStart = async (appointmentId) => {
    const success = await handleStartService(appointmentId);
    if (success) {
      showSuccess('Consultation started successfully!');
    } else {
      showError('Failed to start consultation');
    }
  };

  const ensureAppointmentStarted = useCallback(async () => {
    if (!selectedPatient) return true;

    const currentStatus = (selectedPatient.status || selectedPatient.queueStatus || selectedPatient.appointmentStatus || '').toLowerCase();
    if (currentStatus === 'inprogress') {
      return true;
    }

    const appointmentId = extractPatientId(selectedPatient);
    if (!appointmentId) {
      return true;
    }

    const started = await handleStartService(appointmentId);
    if (started) {
      setSelectedPatient((prev) => {
        if (!prev) return prev;
        const prevId = extractPatientId(prev);
        if (prevId !== appointmentId) {
          return prev;
        }
        return { ...prev, status: 'InProgress' };
      });
      return true;
    }

    return false;
  }, [selectedPatient, extractPatientId, handleStartService]);

  // Handle patient data save
  const handlePatientSave = useCallback(async () => {
    if (!selectedPatient) {
      showWarning('Please select a patient first');
      return { success: false, message: 'Please select a patient first' };
    }

    const result = await savePatientDetails(selectedPatient);
    if (result.success) {
      markFrontPageClean(selectedPatient);
      showSuccess(result.message || 'Patient information updated successfully!');
      await ensureAppointmentStarted();
      await fetchQueue();
    } else {
      showError(result.message || 'Failed to update patient information');
    }

    return result;
  }, [selectedPatient, savePatientDetails, markFrontPageClean, showSuccess, showError, showWarning, ensureAppointmentStarted, fetchQueue]);

  // Handle prescription save
  const handlePrescriptionSave = useCallback(async () => {
    const result = await handleSavePrescription(selectedPatient, user?.id);
    // If save succeeded, mark prescription as clean so the unsaved-change blocker won't appear
    if (result) {
      // Mark the current prescription state as clean after successful save
      // Use setTimeout to ensure this runs after any state updates from the save operation
      setTimeout(() => {
        markPrescriptionClean(selectedPatient, prescriptionData);
      }, 0);
    }
    return result;
  }, [handleSavePrescription, selectedPatient, user?.id, prescriptionData, markPrescriptionClean]);

  const handleSaveAllAndContinue = useCallback(async () => {
    try {
      setUnsavedActionLoading(true);

      // Save form changes if dirty
      if (isFrontPageDirty) {
        const result = await handlePatientSave();
        if (!result?.success) {
          setUnsavedActionLoading(false);
          return;
        }
      }

      // Save prescription if dirty
      if (isPrescriptionDirty) {
        const saved = await handlePrescriptionSave();
        if (!saved) {
          setUnsavedActionLoading(false);
          showError('Failed to save prescription');
          return;
        }
        markPrescriptionClean(selectedPatient, prescriptionData);
      }

      // Note: Vitals and patient details are saved immediately in CollapsiblePatientCard
      // So we just need to reset the flags here
      setHasUnsavedVitals(false);
      setHasUnsavedPatientDetails(false);
      setIsPrescriptionDirty(false);

      const targetTab = pendingTab;
      const targetPatient = pendingPatient;
      closeUnsavedModal();

      // Handle tab change if pending
      if (targetTab) {
        setActiveTab(targetTab);
      }

      // Handle patient change if pending
      if (targetPatient) {
        await proceedWithPatientSelection(targetPatient);
      }
    } catch (error) {
      console.error('Error saving changes:', error);
      setUnsavedActionLoading(false);
    }
  }, [pendingTab, pendingPatient, isFrontPageDirty, isPrescriptionDirty, handlePatientSave, handlePrescriptionSave, closeUnsavedModal, markPrescriptionClean, selectedPatient, prescriptionData, showError]);

  // Keep old name for backward compatibility
  const handleUnsavedSaveAndContinue = handleSaveAllAndContinue;

  // Handle service completion with cleanup and toast notification
  const handleServiceComplete = async (appointmentId) => {
    const success = await handleCompleteService(appointmentId);
    if (success) {
      showSuccess('Consultation completed successfully!');
      setSelectedPatient(null);
      resetPatientData();
    } else {
      showError('Failed to complete consultation');
    }
  };

  // Handle patient creation
  // Handle patient creation
  const handlePatientCreate = async () => {
    const result = await handleCreatePatient(user?.id);
    if (result.success) {
      showSuccess(result.message || 'New patient created successfully!');
      setSelectedPatient(null);
    } else {
      showError(result.message || 'Failed to create patient');
    }
  };

  // Clear selection handler
  const handleClearSelection = () => {
    setSelectedPatient(null);
    resetPatientData();
  };

  // Render tab content
  const renderTabContent = () => {
    const commonProps = {
      selectedPatient,
      user,
      loading: patientLoading || prescriptionLoading
    };

    switch (activeTab) {
      case 'frontpage':
        return (
          <FrontPageTab
            {...commonProps}
            patientData={patientData}
            setPatientData={setPatientData}
            onClearSelection={handleClearSelection}
            onSavePatient={handlePatientSave}
            onCreatePatient={handlePatientCreate}
            updateAppointmentComplaint={updateAppointmentComplaint}
            currentAppointmentId={currentAppointmentId}
            calculateAge={calculateAge}
            onVitalsChangeStart={() => setHasUnsavedVitals(true)}
            onVitalsSaved={async () => {
              console.log('🔄 [DoctorDashboard] onVitalsSaved callback triggered');
              setHasUnsavedVitals(false);

              if (selectedPatient) {
                const appointmentId = selectedPatient._id || selectedPatient.id;
                console.log('🔄 [DoctorDashboard] Fetching fresh appointment:', appointmentId);

                try {
                  // Fetch the fresh appointment data directly
                  const response = await appointmentAPI.getById(appointmentId);
                  console.log('🔄 [DoctorDashboard] Fresh appointment response:', response);

                  if (response.success && response.data) {
                    // Update selectedPatient with fresh data
                    setSelectedPatient(response.data);
                    console.log('🔄 [DoctorDashboard] Updated selectedPatient with fresh data');

                    // Reload patient data with the fresh appointment
                    await loadPatientData(response.data);
                    console.log('🔄 [DoctorDashboard] Patient data reloaded');
                  }

                  // Refresh queue to update the sidebar
                  await fetchQueue();
                  console.log('🔄 [DoctorDashboard] Queue refreshed');
                } catch (error) {
                  console.error('🔄 [DoctorDashboard] Error refreshing appointment:', error);
                }
              }
            }}
            onPatientDetailsChangeStart={() => setHasUnsavedPatientDetails(true)}
            onPatientDetailsSaved={async () => {
              console.log('🔄 [DoctorDashboard] onPatientDetailsSaved callback triggered');
              setHasUnsavedPatientDetails(false);

              if (selectedPatient) {
                try {
                  // Refresh queue first to get updated patient names
                  await fetchQueue();
                  console.log('🔄 [DoctorDashboard] Queue refreshed');

                  // Then reload the patient data
                  await loadPatientData(selectedPatient);
                  console.log('🔄 [DoctorDashboard] Patient data reloaded');
                } catch (error) {
                  console.error('🔄 [DoctorDashboard] Error refreshing patient:', error);
                }
              }
            }}
            onPatientDataRefresh={() => {
              // Refresh queue to update patient names
              fetchQueue();
              // Reload patient data
              if (selectedPatient) {
                loadPatientData(selectedPatient);
              }
            }}
          />
        );

      case 'availability':
        console.log('Rendering AvailabilityTab with user:', user);
        return (
          <AvailabilityTab
            doctorId={user?._id || user?.id}
            user={user}
            selectedPatient={selectedPatient}
          />
        );

      case 'prescription':
        return (
          <PrescriptionTab
            {...commonProps}
            prescriptionData={prescriptionData}
            setPrescriptionData={setPrescriptionData}
            previousPrescriptions={previousPrescriptions}
            loadingPrescriptions={loadingPrescriptions}
            diseases={diseases}
            slogans={slogans}
            medicines={medicines}
            tests={tests}
            imagingTests={imagingTests}
            loadingMasterData={loadingMasterData}
            onAddMedication={addMedicationItem}
            onRemoveMedication={removeMedicationItem}
            onUpdateMedication={updateMedicationItem}
            onSavePrescription={handlePrescriptionSave}
            onPrintPrescription={handlePrintPrescription}
          />
        );

      case 'doctor-opinion':
        return (
          <DoctorOpinionTab
            patientId={selectedPatient?.patient?._id || selectedPatient?.patient || ''}
          />
        );

      case 'reference':
        return (
          <ReferenceTab
            patient={
              typeof selectedPatient?.patient === 'object'
                ? selectedPatient?.patient
                : selectedPatient?.patient
                  ? { _id: selectedPatient.patient }
                  : undefined
            }
          />
        );
      
      case 'all-patients':
        return <PatientDetails />;

      case 'profile':
        return <Profile />;

      default:
        return (
          <div className="bg-white rounded-lg border p-6">
            <p className="text-gray-500">Content for {activeTab} tab</p>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <NavigationPreventer />
      <ToastContainer toasts={toasts} removeToast={removeToast} />

      {/* Top Navigation Bar */}
      <TopNavBar
        user={user}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
      />

      {/* Main Content Area */}
      <div className="flex flex-col h-[calc(100vh-64px)]">
        {/* Content - Scrollable area */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="bg-white rounded-xl border-2 border-gray-200 shadow-sm">
            <div className="p-5">
              {renderTabContent()}
            </div>
          </div>
        </div>

        {/* Ultra Compact Patient Queue at Bottom */}
        <div className="flex-shrink-0 p-2 bg-gray-50 border-t border-gray-200">
          <HorizontalPatientQueue
            patientQueue={patientQueue}
            queueStats={queueStats}
            loading={queueLoading}
            selectedPatient={selectedPatient}
            onPatientSelect={handlePatientSelect}
            onStartService={handleServiceStart}
            onCompleteService={handleServiceComplete}
            onRefresh={fetchQueue}
          />
        </div>
      </div>

      {showUnsavedModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <div className="space-y-2">
              <h3 className="text-xl font-semibold text-black">
                {unsavedChangeType === 'vitals' && 'Unsaved Vitals Changes'}
                {unsavedChangeType === 'details' && 'Unsaved Patient Details'}
                {unsavedChangeType === 'form' && 'Unsaved Patient Information'}
                {unsavedChangeType === 'prescription' && 'Unsaved Prescription Changes'}
                {unsavedChangeType === 'multiple' && 'Unsaved Changes'}
              </h3>
              <p className="text text-black font-semibold text">
                You have unsaved changes. What would you like to do?
              </p>
            </div>

            <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end sm:gap-3">
              <button
                onClick={closeUnsavedModal}
                disabled={unsavedActionLoading}
                className="rounded-lg border border-gray-200 px-4 py-2 text-lg font-semibold text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleDiscardAllChanges}
                disabled={unsavedActionLoading}
                className="rounded-lg border border-red-200 px-4 py-2 text-lg font-semibold text-red-600 hover:bg-red-50 disabled:cursor-not-allowed"
              >
                Discard changes
              </button>
              <button
                onClick={handleSaveAllAndContinue}
                disabled={unsavedActionLoading}
                className="rounded-lg bg-[#2D869C] px-4 py-2 text-lg font-semibold text-white hover:bg-[#236b7a] disabled:cursor-not-allowed disabled:bg-gray-300"
              >
                {unsavedActionLoading ? 'Saving...' : 'Save & Continue'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DoctorDashboard;
